import React from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import CheckIcon from '@mui/icons-material/Check';
import { Alert } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useGetAccount } from '~/hooks/apis/offers/useGetAccount';

import AccountListPageHeader from '../AccountListPageHeader';
import ContactMethodLink from '../Contacts/ContactMethodLink';
import { useAccountRelatedFullList } from '../useAccountRelatedRows';

import { toAccountRelatedContact } from './toAccountRelatedContact';
import type { AccountRelatedContact } from './types';

const sharedOpts: Partial<GridColDef<AccountRelatedContact>> = {
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
};

const columns: Array<GridColDef<AccountRelatedContact>> = [
  { field: 'name', headerName: 'Contact Name', flex: 1, minWidth: 180, ...sharedOpts },
  {
    field: 'accountName',
    headerName: 'Account Name',
    flex: 1,
    minWidth: 200,
    renderCell: ({ row }) => row.accountName || '—',
    ...sharedOpts,
  },
  {
    field: 'title',
    headerName: 'Title',
    flex: 1,
    minWidth: 180,
    renderCell: ({ row }) => row.title || '—',
    ...sharedOpts,
  },
  {
    field: 'mobilePhone',
    headerName: 'Mobile',
    flex: 1,
    minWidth: 150,
    renderCell: ({ row }) => row.mobilePhone || '—',
    ...sharedOpts,
  },
  {
    field: 'isPrimary',
    headerName: 'Primary',
    type: 'boolean',
    width: 90,
    renderCell: ({ row }) => (row.isPrimary ? <CheckIcon fontSize="small" titleAccess="Primary" /> : null),
    ...sharedOpts,
  },
  {
    field: 'isActive',
    headerName: 'Active',
    type: 'boolean',
    width: 90,
    renderCell: ({ row }) => (row.isActive ? <CheckIcon fontSize="small" titleAccess="Active" /> : null),
    ...sharedOpts,
  },
  {
    field: 'email',
    headerName: 'Email',
    flex: 1,
    minWidth: 220,
    renderCell: ({ row }) => <ContactMethodLink kind="email" value={row.email} />,
    ...sharedOpts,
  },
  {
    field: 'phone',
    headerName: 'Phone',
    flex: 1,
    minWidth: 150,
    renderCell: ({ row }) => <ContactMethodLink kind="phone" value={row.phone} />,
    ...sharedOpts,
  },
  {
    field: 'roles',
    headerName: 'Roles',
    flex: 1,
    minWidth: 180,
    sortable: false,
    renderCell: ({ row }) => row.roles.join(', ') || '—',
    ...sharedOpts,
  },
  {
    field: 'accountRecordType',
    headerName: 'Account Record Type',
    flex: 1,
    minWidth: 180,
    renderCell: ({ row }) => row.accountRecordType || '—',
    ...sharedOpts,
  },
];

export default function AccountRelatedContactsListPage() {
  const { id_account: accountId } = useParams<{ id_account: string }>();
  const { data: account } = useGetAccount(accountId);
  const {
    count,
    error,
    isLoading,
    isTruncated,
    rows: relatedContacts,
  } = useAccountRelatedFullList(accountId, 'related_contacts', toAccountRelatedContact);

  return (
    <>
      <Helmet>
        <title>Related Contacts | {account?.name || accountId}</title>
      </Helmet>

      <AccountListPageHeader
        accountId={accountId}
        count={count}
        isTruncated={isTruncated}
        sortedBy="Contact Name"
        title="Related Contacts"
      />

      {error ? (
        <Alert severity="error">Could not load related contacts: {error.message}</Alert>
      ) : (
        <DataGrid
          autoHeight
          disableRowSelectionOnClick
          columns={columns}
          loading={isLoading}
          rows={relatedContacts}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
            sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
          }}
          pageSizeOptions={[25, 50, 100]}
        />
      )}
    </>
  );
}
